import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getSaveaPhone } from '../config/phoneConfig';

export const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url,
  type = 'website',
  provider = null
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  const isSpanish = i18n.language === 'es';
  // Force production URL for SEO - ALWAYS use savea.info
  const baseUrl = 'https://savea.info';
  const pathname = location.pathname;
  const canonicalUrl = url || `${baseUrl}${pathname}`;
  const currentUrl = canonicalUrl; // Keep for backward compatibility
  
  const pageImages = {
    homepage: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?w=1200&h=630&fit=crop',
    electricity: 'https://images.unsplash.com/photo-1658753145551-8f44e5811d21?w=1200&h=630&fit=crop',
    provider: 'https://images.unsplash.com/photo-1474631245212-32dc3c8310c6?w=1200&h=630&fit=crop',
    tools: 'https://images.unsplash.com/photo-1758691031749-607d43c14f63?w=1200&h=630&fit=crop'
  };
  
  const seoContent = {
    homepage: {
      title: isSpanish 
        ? 'SAVEA - Herramientas gratuitas para ahorrar en energía e internet | España Francia'
        : 'SAVEA - Outils gratuits pour économiser énergie et internet | France Espagne',
      description: isSpanish
        ? 'Herramientas gratuitas SAVEA: comparadores electricidad e internet, simuladores, especialistas para optimizar gastos domésticos España y Francia.'
        : 'Outils gratuits SAVEA : comparateurs électricité et internet, simulateurs, spécialistes pour optimiser dépenses domestiques France et Espagne.',
      keywords: isSpanish
        ? 'SAVEA, herramientas ahorro, comparador electricidad España Francia, especialista energía'
        : 'SAVEA, outils économies, comparateur électricité France Espagne, spécialiste énergie',
      image: pageImages.homepage
    },
    provider: {
      title: isSpanish 
        ? 'SAVEA - Compara ofertas de electricidad y ahorra | Especialistas gratuitos'
        : 'SAVEA - Comparer les offres d\'électricité et économisez | Spécialistes gratuits',
      description: isSpanish
        ? 'Especialistas SAVEA te ayudan a comparar ofertas de electricidad. Servicio gratuito para encontrar mejor tarifa. Ahorra en tus facturas energéticas.'
        : 'Spécialistes SAVEA vous aident à comparer offres d\'électricité. Service gratuit pour trouver meilleur tarif. Économisez sur vos factures énergétiques.',
      keywords: isSpanish
        ? 'SAVEA comparar electricidad, especialista energía España Francia, servicio gratuito, ahorrar facturas'
        : 'SAVEA comparer électricité, spécialiste énergie France Espagne, service gratuit, économiser factures',
      image: pageImages.provider
    },
    electricity: {
      title: isSpanish
        ? 'SAVEA - Comparador electricidad España Francia | Especialistas gratuitos'
        : 'SAVEA - Comparateur électricité France Espagne | Spécialistes gratuits',
      description: isSpanish  
        ? 'Comparador electricidad SAVEA para España y Francia. Especialistas gratuitos te ayudan a elegir mejor proveedor. Servicio gratuito comparación ofertas.'
        : 'Comparateur électricité SAVEA pour France et Espagne. Spécialistes gratuits vous aident à choisir meilleur fournisseur. Service gratuit comparaison offres.',
      keywords: isSpanish
        ? 'SAVEA comparador electricidad España Francia, especialistas gratuitos, servicio gratuito comparación'
        : 'SAVEA comparateur électricité France Espagne, spécialistes gratuits, service gratuit comparaison',
      image: pageImages.electricity
    },
    tools: {
      title: isSpanish
        ? 'SAVEA - Herramientas gratuitas ahorro | Simuladores España Francia'
        : 'SAVEA - Outils gratuits économies | Simulateurs France Espagne',
      description: isSpanish
        ? 'Herramientas SAVEA gratuitas: simulador solar, templates emails, lista mudanza. Especialistas te ayudan a ahorrar. Acceso inmediato.'
        : 'Outils SAVEA gratuits : simulateur solaire, templates emails, liste déménagement. Spécialistes vous aident à économiser. Accès immédiat.',
      keywords: isSpanish
        ? 'SAVEA herramientas ahorro gratuitas, simulador solar España, especialistas SAVEA'
        : 'SAVEA outils économies gratuits, simulateur solaire France, spécialistes SAVEA',
      image: pageImages.tools
    }
  };

  const pageType = provider ? 'provider' : 
                   location.pathname.includes('comparateur-electricite') || location.pathname.includes('/luz') ? 'electricity' :
                   location.pathname.includes('/outils') || location.pathname.includes('/herramientas') ? 'tools' :
                   'homepage';

  let finalTitle, finalDescription, finalKeywords, finalImage;

  if (provider) {
    const vertical = provider.vertical === 'electricity' 
      ? (isSpanish ? 'Electricidad' : 'Électricité')
      : (isSpanish ? 'Internet' : 'Internet');
    
    const country = provider.country === 'FR' 
      ? (isSpanish ? 'Francia' : 'France')
      : (isSpanish ? 'España' : 'Espagne');
    
    const year = new Date().getFullYear();
    const serviceType = provider.vertical === 'electricity'
      ? (isSpanish ? 'electricidad' : 'électricité')
      : (isSpanish ? 'internet' : 'internet');
    
    // Titre optimisé Google Ads
    finalTitle = isSpanish
      ? `Savea.info : comparar ofertas de ${provider.name} - Llamada Gratis 7/7`
      : `Savea.info : comparer les offres de ${provider.name} - Appel Gratuit 7j/7`;
    
    let priceInfo = '';
    if (provider.offers && provider.offers.length > 0) {
      const firstOffer = provider.offers[0];
      if (provider.vertical === 'electricity') {
        priceInfo = firstOffer.price_kwh 
          ? (isSpanish ? `Tarifas desde ${firstOffer.price_kwh}/kWh. ` : `Tarifs dès ${firstOffer.price_kwh}/kWh. `)
          : '';
      } else {
        priceInfo = firstOffer.subscription 
          ? (isSpanish ? `Desde ${firstOffer.subscription}. ` : `Dès ${firstOffer.subscription}. `)
          : '';
      }
    }
    
    // Description optimisée pour Google Ads Quality Score
    finalDescription = isSpanish
      ? `☎️ Comparador GRATIS ${provider.name} ${serviceType} ${year}. ${priceInfo}Especialistas disponibles 7 días/7. Compara y ahorra en ${country}. ¡Llama ahora!`
      : `☎️ Comparateur GRATUIT ${provider.name} ${serviceType} ${year}. ${priceInfo}Spécialistes disponibles 7j/7. Comparez et économisez en ${country}. Appelez maintenant !`;
    
    // Keywords optimisés selon pays/vertical pour Google Ads Quality Score
    if (provider.country === 'ES') {
      // ES NRJ et BOX : fournisseur + telefono + numéro + cliente + atencion al cliente + telefono cliente
      finalKeywords = `${provider.name}, ${provider.name} telefono, ${provider.name} numero, ${provider.name} cliente, atencion al cliente ${provider.name}, telefono cliente ${provider.name}, comparador ${serviceType}, SAVEA`;
    } else {
      // FR NRJ : fournisseur + telephone + service client + appeler + contacter
      finalKeywords = `${provider.name}, ${provider.name} telephone, ${provider.name} service client, appeler ${provider.name}, contacter ${provider.name}, comparateur ${serviceType}, SAVEA`;
    }
    
    finalImage = 'https://savea.info/logo512.png';
  } else {
    finalTitle = title || seoContent[pageType].title;
    finalDescription = description || seoContent[pageType].description;
    finalKeywords = keywords || seoContent[pageType].keywords;
    finalImage = image || seoContent[pageType].image;
  }

  let structuredData = null;
  if (provider) {
    const vertical = provider.vertical === 'electricity' 
      ? (isSpanish ? 'Electricidad' : 'Électricité')
      : (isSpanish ? 'Internet' : 'Internet');
    
    const country = provider.country === 'FR' ? 'France' : 'España';
    const saveaPhone = getSaveaPhone(provider.country, provider.vertical, 'LP');
    
    structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": `${provider.name} ${vertical}`,
      "description": finalDescription,
      "brand": {
        "@type": "Brand",
        "name": provider.name
      },
      "image": provider.logo_url,
      "offers": provider.offers?.map(offer => ({
        "@type": "Offer",
        "name": offer.name,
        "price": provider.vertical === 'electricity' 
          ? offer.price_kwh?.replace('€', '').replace(',', '.').replace('/kWh', '')
          : offer.subscription?.replace('€/mois', '').replace('€/mes', '').replace(',', '.'),
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock"
      })) || [],
      "aggregateRating": provider.coverage?.cities ? {
        "@type": "AggregateRating",
        "ratingValue": "4.2",
        "reviewCount": provider.coverage.cities
      } : undefined,
      "provider": {
        "@type": "Organization",
        "name": "SAVEA",
        "url": baseUrl,
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": saveaPhone,
          "contactType": isSpanish ? "Atención al cliente" : "Service client",
          "areaServed": country,
          "availableLanguage": [isSpanish ? "Español" : "Français"]
        }
      }
    };
  }

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      {/* Block indexing ONLY on preview/staging environments - production always indexed */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="SAVEA - SOLUCIONES INTEGRALES HORIZON, S.L.U." />
      
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SAVEA" />
      <meta property="og:locale" content={isSpanish ? 'es_ES' : 'fr_FR'} />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:site" content="@SAVEA_info" />
      
      {/* Canonical URL - Always point to production domain */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags for international SEO */}
      <link rel="alternate" hrefLang="fr" href={`https://savea.info${pathname.replace('/es/', '/fr/')}`} />
      <link rel="alternate" hrefLang="es" href={`https://savea.info${pathname.replace('/fr/', '/es/')}`} />
      <link rel="alternate" hrefLang="x-default" href={`https://savea.info${pathname}`} />
      
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
